import {
  createContext,
  useContext,
  useState,
  useRef,
  ReactNode,
  useEffect,
  useCallback,
} from 'react';
import type { ConferenceEvent } from '@conference/shared';
import { authService } from '../services/authService';
import type {
  PolicyConfirmationsValue,
  PolicyStatus,
  UserProfile,
} from '../services/authService';
import { eventService, noteService, membershipService } from '../services/dataService';
import { draftAgentMessage } from '../services/aiService';

function sameConferenceEvent(
  current: ConferenceEvent | null,
  loaded: ConferenceEvent,
): boolean {
  return current?.id === loaded.id && JSON.stringify(current) === JSON.stringify(loaded);
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SavedNote {
  id: string;
  title: string;
  content: string;
  source: 'manual' | 'ai-report' | 'ai-session-prep' | 'ai-paper-insight' | 'ai-networking' | 'ai-copilot';
  sourceLabel?: string;
  sessionId?: string;
  sessionTitle?: string;
  paperId?: string;
  paperTitle?: string;
  attendeeId?: string;
  attendeeName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AIChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface SavedAIChat {
  id: string;
  mode: string;
  title: string;
  messages: AIChatMessage[];
  createdAt: string;
}

type ThemeMode = 'dark' | 'light';

export interface AIPreferences {
  aiSuggestions: boolean;
  aiAutoInsights: boolean;
}

const DEFAULT_AI_PREFS: AIPreferences = { aiSuggestions: true, aiAutoInsights: true };

export interface AuthSessionSnapshot {
  readonly userId: string;
  readonly generation: number;
  readonly signal: AbortSignal;
}

export type AgentActionType =
  | 'schedule-meeting'
  | 'draft-followup'
  | 'block-time'
  | 'add-linkedin'
  | 'send-intro'
  | 'save-reading-list'
  | 'export-recap';

export type AgentActionStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface AgentAction {
  id: string;
  type: AgentActionType;
  status: AgentActionStatus;
  title: string;
  description: string;
  icon: string;
  metadata?: Record<string, string>;
  createdAt: string;
  completedAt?: string;
}

/**
 * performAgentAction — actually carries out an Agent Action and leaves a real,
 * persistent artifact in My Notes. Draft actions generate genuine AI text; the
 * rest save a structured, checkable note. Throws on failure so the caller can
 * surface a real error instead of a fake "completed".
 */
async function performAgentAction(
  action: Omit<AgentAction, 'id' | 'status' | 'createdAt' | 'completedAt'>,
  deps: {
    addNote: (
      note: Omit<SavedNote, 'id' | 'createdAt' | 'updatedAt'>,
      owner: AuthSessionSnapshot,
    ) => SavedNote | null;
    eventId: string | null;
    session: AuthSessionSnapshot;
    isSessionCurrent: (session: AuthSessionSnapshot) => boolean;
  },
): Promise<void> {
  const m = action.metadata ?? {};
  const { addNote, eventId, session, isSessionCurrent } = deps;
  const ensureCurrent = () => {
    if (!isSessionCurrent(session)) {
      throw new Error('Your account session changed. Please try again.');
    }
  };
  const saveNote = (
    note: Omit<SavedNote, 'id' | 'createdAt' | 'updatedAt'>,
  ) => {
    ensureCurrent();
    if (!addNote(note, session)) {
      throw new Error('Your account session changed. Please try again.');
    }
  };

  switch (action.type) {
    case 'draft-followup':
    case 'schedule-meeting':
    case 'send-intro': {
      const kind =
        action.type === 'draft-followup'
          ? 'follow-up'
          : action.type === 'schedule-meeting'
          ? 'coffee-invite'
          : 'intro';
      ensureCurrent();
      const text = await draftAgentMessage(kind, {
        entityId: m.attendeeId || m.entityId,
        name: m.name,
        eventId: eventId ?? undefined,
      }, { signal: session.signal });
      ensureCurrent();
      saveNote({
        title: action.title || 'AI draft',
        content: text,
        source: 'ai-copilot',
        sourceLabel: 'AI Copilot draft',
        attendeeId: m.attendeeId || m.entityId,
        attendeeName: m.name,
      });
      return;
    }
    case 'block-time': {
      const duration = m.duration || '30';
      const topic = m.topic || 'Focus time';
      saveNote({
        title: `${duration} min — ${topic}`,
        content: `Planned ${duration} minutes for: ${topic}.\n\n- [ ] Start\n- [ ] Capture key takeaways`,
        source: 'ai-copilot',
        sourceLabel: 'Planned time block',
        sessionId: m.sessionId,
        sessionTitle: m.sessionTitle,
        paperId: m.paperId,
        paperTitle: m.paperTitle,
      });
      return;
    }
    case 'save-reading-list': {
      const title = m.paperTitle || m.title || 'Saved paper';
      saveNote({
        title: `Reading list — ${title}`,
        content: m.note || `Added "${title}" to your reading list.`,
        source: 'ai-copilot',
        sourceLabel: 'Reading list',
        paperId: m.paperId,
        paperTitle: m.paperTitle,
      });
      return;
    }
    case 'add-linkedin': {
      const name = m.name || 'this contact';
      saveNote({
        title: `Connect on LinkedIn — ${name}`,
        content: m.linkedin ? `Connect with ${name}: ${m.linkedin}` : `Remember to connect with ${name} on LinkedIn.`,
        source: 'ai-copilot',
        sourceLabel: 'Networking reminder',
        attendeeName: m.name,
      });
      return;
    }
    case 'export-recap': {
      saveNote({
        title: m.title || 'Conference recap',
        content: m.content || 'Your conference recap has been saved to My Notes.',
        source: 'ai-report',
        sourceLabel: 'AI Conference Recap',
      });
      return;
    }
    default:
      throw new Error("That action isn't available yet.");
  }
}

// Legacy user shape used by existing screens (maps to UserProfile)
export interface LegacyUser {
  id: string;
  name: string;
  email: string;
  title?: string;
  company?: string;
  bio?: string;
  linkedin?: string;
  twitter?: string;
  avatar?: string;
}

export interface AppContextType {
  // Auth
  authUser: UserProfile | null;
  profile: UserProfile | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  policyStatus: PolicyStatus | null;
  authSession: AuthSessionSnapshot | null;
  isAuthSessionCurrent: (session: AuthSessionSnapshot) => boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (
    email: string,
    password: string,
    name: string,
    confirmations: PolicyConfirmationsValue,
  ) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signInWithApple: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  deleteAccount: (
    confirmEmail: string,
    owner: AuthSessionSnapshot,
  ) => Promise<void>;
  updateProfile: (
    updates: Partial<UserProfile>,
    owner: AuthSessionSnapshot,
  ) => Promise<{ error: Error | null }>;
  refreshUser: (owner: AuthSessionSnapshot) => Promise<void>;
  applyUploadedAvatar: (url: string, owner: AuthSessionSnapshot) => void;
  acceptCurrentPolicy: (owner: AuthSessionSnapshot) => Promise<void>;
  refreshPolicyStatus: (owner: AuthSessionSnapshot) => Promise<void>;
  // ── Backwards-compatible aliases (used by existing screens) ──
  user: LegacyUser | null;
  setUser: (u: LegacyUser | null) => void;
  login: (u: LegacyUser) => void;
  logout: () => void;
  currentEvent: string | null;
  setCurrentEvent: (id: string | null, event?: ConferenceEvent | null) => void;
  // Event selection
  currentEventId: string | null;
  setCurrentEventId: (id: string | null, event?: ConferenceEvent | null) => void;
  selectedEvent: ConferenceEvent | null;
  isSelectedEventLoading: boolean;
  // Membership — true only if user has a confirmed registration for currentEventId
  isCurrentEventMember: boolean;
  memberEventIds: Set<string>;
  refreshMembership: (owner: AuthSessionSnapshot) => Promise<void>;
  // Theme
  theme: ThemeMode;
  toggleTheme: () => void;
  setTheme: (t: ThemeMode) => void;
  // Notes (local optimistic cache — synced to API via hooks)
  notes: SavedNote[];
  addNote: (
    note: Omit<SavedNote, 'id' | 'createdAt' | 'updatedAt'>,
    owner: AuthSessionSnapshot,
  ) => SavedNote | null;
  updateNote: (
    id: string,
    updates: Partial<SavedNote>,
    owner: AuthSessionSnapshot,
  ) => void;
  deleteNote: (id: string, owner: AuthSessionSnapshot) => void;
  getNoteById: (id: string) => SavedNote | undefined;
  // AI Chat History (local cache)
  savedChats: SavedAIChat[];
  saveChat: (
    chat: Omit<SavedAIChat, 'id' | 'createdAt'>,
    owner: AuthSessionSnapshot,
  ) => void;
  // AI Preferences
  aiPreferences: AIPreferences;
  setAiPreference: <K extends keyof AIPreferences>(key: K, value: AIPreferences[K]) => void;
  // Agent Actions (ephemeral UI state)
  agentActions: AgentAction[];
  executeAgentAction: (
    action: Omit<AgentAction, 'id' | 'status' | 'createdAt' | 'completedAt'>,
    owner: AuthSessionSnapshot,
  ) => Promise<AgentAction>;
  dismissAgentAction: (id: string) => void;
  clearCompletedActions: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppContextValueProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: AppContextType;
}) {
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AppProvider({ children }: { children: ReactNode }) {
  // ── Auth state ──
  const [authUser, setAuthUser] = useState<UserProfile | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [policyStatus, setPolicyStatus] = useState<PolicyStatus | null>(null);

  // ── App state ──
  const [currentEventId, setCurrentEventId] = useState<string | null>(() => {
    try { return localStorage.getItem('current-event-id'); } catch { return null; }
  });
  const [selectedEvent, setSelectedEvent] = useState<ConferenceEvent | null>(null);
  const [isSelectedEventLoading, setIsSelectedEventLoading] = useState(
    () => Boolean(currentEventId),
  );

  const selectCurrentEvent = useCallback((id: string | null, event?: ConferenceEvent | null) => {
    setCurrentEventId(id);
    setSelectedEvent(event?.id === id ? event : (current) => current?.id === id ? current : null);
    setIsSelectedEventLoading(Boolean(id) && event?.id !== id);
  }, []);

  const [theme, setThemeState] = useState<ThemeMode>(() => {
    try { return (localStorage.getItem('app-theme') as ThemeMode) || 'dark'; } catch { return 'dark'; }
  });

  const [notes, setNotes] = useState<SavedNote[]>(() => {
    try { const s = localStorage.getItem('app-notes'); return s ? JSON.parse(s) : []; } catch { return []; }
  });

  const [savedChats, setSavedChats] = useState<SavedAIChat[]>(() => {
    try { const s = localStorage.getItem('app-ai-chats'); return s ? JSON.parse(s) : []; } catch { return []; }
  });

  const [aiPreferences, setAiPreferences] = useState<AIPreferences>(() => {
    try { const s = localStorage.getItem('app-ai-prefs'); return s ? JSON.parse(s) : DEFAULT_AI_PREFS; } catch { return DEFAULT_AI_PREFS; }
  });

  const [agentActions, setAgentActions] = useState<AgentAction[]>([]);
  const [memberEventIds, setMemberEventIds] = useState<Set<string>>(new Set());
  const [authSession, setAuthSession] = useState<AuthSessionSnapshot | null>(null);
  const authGenerationRef = useRef(0);
  const activeAuthSessionRef = useRef<AuthSessionSnapshot | null>(null);
  const sessionAbortControllerRef = useRef<AbortController | null>(null);

  const isAuthSessionCurrent = useCallback(
    (session: AuthSessionSnapshot) => {
      const active = activeAuthSessionRef.current;
      return (
        !session.signal.aborted &&
        active?.userId === session.userId &&
        active.generation === session.generation
      );
    },
    [],
  );

  const beginAuthSession = useCallback((userId: string) => {
    sessionAbortControllerRef.current?.abort();
    const controller = new AbortController();
    sessionAbortControllerRef.current = controller;
    authGenerationRef.current += 1;
    const next: AuthSessionSnapshot = {
      userId,
      generation: authGenerationRef.current,
      signal: controller.signal,
    };
    activeAuthSessionRef.current = next;
    setAuthSession(next);
    return next;
  }, []);

  const invalidateAuthSession = useCallback(() => {
    sessionAbortControllerRef.current?.abort();
    sessionAbortControllerRef.current = null;
    authGenerationRef.current += 1;
    activeAuthSessionRef.current = null;
    setAuthSession(null);
  }, []);

  const beginAuthTransition = useCallback(() => {
    sessionAbortControllerRef.current?.abort();
    const controller = new AbortController();
    sessionAbortControllerRef.current = controller;
    authGenerationRef.current += 1;
    activeAuthSessionRef.current = null;
    setAuthSession(null);
    setPolicyStatus(null);
    setCurrentEventId(null);
    setMemberEventIds(new Set());
    return {
      generation: authGenerationRef.current,
      signal: controller.signal,
    };
  }, []);

  const refreshMembership = useCallback(async (session: AuthSessionSnapshot) => {
    if (!isAuthSessionCurrent(session)) return;
    try {
      const events = await membershipService.listMyEvents({
        signal: session.signal,
      });
      if (isAuthSessionCurrent(session)) {
        setMemberEventIds(new Set(events.map(e => e.id)));
      }
    } catch { /* non-fatal */ }
  }, [isAuthSessionCurrent]);

  useEffect(() => {
    if (!currentEventId) {
      setSelectedEvent(null);
      setIsSelectedEventLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    let requestSequence = 0;
    const eventId = currentEventId;
    setSelectedEvent((current) => current?.id === eventId ? current : null);
    setIsSelectedEventLoading(true);

    const refreshSelectedEvent = (initial = false) => {
      const request = ++requestSequence;
      eventService.getById(eventId, { signal: controller.signal })
        .then((loaded) => {
          if (active && request === requestSequence && loaded?.id === eventId) {
            setSelectedEvent((current) => sameConferenceEvent(current, loaded) ? current : loaded);
          }
        })
        .catch((error) => {
          if (active && request === requestSequence && !controller.signal.aborted) {
            console.error('Failed to load selected event', error);
            if (initial) setSelectedEvent(null);
          }
        })
        .finally(() => {
          if (active && request === requestSequence) {
            setIsSelectedEventLoading(false);
          }
        });
    };

    refreshSelectedEvent(true);
    const timer = window.setInterval(() => refreshSelectedEvent(), 60_000);
    const onFocus = () => refreshSelectedEvent();
    const onConfigurationChanged = () => refreshSelectedEvent();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshSelectedEvent();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('event:configuration_changed', onConfigurationChanged);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      active = false;
      controller.abort();
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('event:configuration_changed', onConfigurationChanged);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [currentEventId]);

  // Maps temp IDs (e.g. 'temp-1234') → real server UUIDs once the API responds.
  // Lets updateNote/deleteNote resolve the real ID even when the caller still holds the stale temp ID.
  const tempToRealId = useRef<Map<string, string>>(new Map());

  // ── Initialise auth from existing JWT ──
  useEffect(() => {
    const startupTimer = setTimeout(() => setIsAuthLoading(false), 8000);
    const startupController = new AbortController();
    const startupGeneration = authGenerationRef.current;

    authService.getCurrentUser({ signal: startupController.signal }).then(async (user) => {
      if (
        startupController.signal.aborted ||
        authGenerationRef.current !== startupGeneration
      ) {
        return;
      }
      let currentPolicy: PolicyStatus | null = null;
      if (user) {
        try {
          currentPolicy = await authService.getPolicyStatus({
            signal: startupController.signal,
          });
        } catch {
          currentPolicy = null;
        }
      }
      if (
        startupController.signal.aborted ||
        authGenerationRef.current !== startupGeneration
      ) {
        return;
      }
      clearTimeout(startupTimer);
      setAuthUser(user);
      setPolicyStatus(currentPolicy);
      setIsAuthLoading(false);
      if (user) {
        const session = beginAuthSession(user.id);
        if (currentPolicy?.accepted) {
          void loadNotesFromAPI(session);
          // Fetch membership list to determine viewer vs member access
          membershipService.listMyEvents({ signal: session.signal }).then((events) => {
            if (isAuthSessionCurrent(session)) {
              setMemberEventIds(new Set(events.map(e => e.id)));
            }
          }).catch(() => { /* non-fatal */ });
        }
      }
    }).catch(() => {
      if (startupController.signal.aborted) return;
      clearTimeout(startupTimer);
      setIsAuthLoading(false);
    });

    // Listen for 401 / token expiry from apiClient
    const handleExpired = () => {
      startupController.abort();
      clearTimeout(startupTimer);
      invalidateAuthSession();
      setIsAuthLoading(false);
      setAuthUser(null);
      setPolicyStatus(null);
      setCurrentEventId(null);
      setNotes([]);
      setSavedChats([]);
      setAgentActions([]);
      setMemberEventIds(new Set());
      tempToRealId.current.clear();
    };
    window.addEventListener('auth:expired', handleExpired);

    return () => {
      startupController.abort();
      sessionAbortControllerRef.current?.abort();
      clearTimeout(startupTimer);
      window.removeEventListener('auth:expired', handleExpired);
    };
  }, [beginAuthSession, invalidateAuthSession, isAuthSessionCurrent]);

  async function loadNotesFromAPI(session: AuthSessionSnapshot) {
    if (!isAuthSessionCurrent(session)) return;
    try {
      const rows = await noteService.getAll(
        session.userId,
        { signal: session.signal },
      );
      const mapped: SavedNote[] = rows.map((n) => ({
        id: n.id,
        title: n.title,
        content: n.content,
        source: n.source,
        sourceLabel: n.source_label,
        sessionId: n.session_id,
        paperId: n.paper_id,
        createdAt: n.created_at,
        updatedAt: n.updated_at,
      }));
      if (isAuthSessionCurrent(session)) {
        setNotes(mapped);
      }
    } catch (err) {
      if (isAuthSessionCurrent(session)) {
        console.error('Failed to load notes', err);
      }
    }
  }

  // ── Persist local state ──
  // Notes are cached locally so they remain readable offline / on cold start
  // before the API round-trip completes (initial state hydrates from this key).
  useEffect(() => { try { localStorage.setItem('app-notes', JSON.stringify(notes)); } catch { /* ignore */ } }, [notes]);
  useEffect(() => { try { localStorage.setItem('app-theme', theme); } catch { /* ignore */ } }, [theme]);
  useEffect(() => { try { localStorage.setItem('app-ai-chats', JSON.stringify(savedChats)); } catch { /* ignore */ } }, [savedChats]);
  useEffect(() => { try { localStorage.setItem('app-ai-prefs', JSON.stringify(aiPreferences)); } catch { /* ignore */ } }, [aiPreferences]);
  useEffect(() => { try { currentEventId ? localStorage.setItem('current-event-id', currentEventId) : localStorage.removeItem('current-event-id'); } catch { /* ignore */ } }, [currentEventId]);

  // ── Auth methods ──
  const signIn = useCallback(async (email: string, password: string) => {
    const transition = beginAuthTransition();
    try {
      await authService.login(email, password, { signal: transition.signal });
      if (
        transition.signal.aborted ||
        authGenerationRef.current !== transition.generation
      ) {
        return { error: new Error('Sign-in was cancelled.') };
      }
      const user = await authService.getCurrentUser({ signal: transition.signal });
      if (
        !user ||
        transition.signal.aborted ||
        authGenerationRef.current !== transition.generation
      ) {
        return { error: new Error('Could not load the signed-in account.') };
      }
      let currentPolicy: PolicyStatus | null = null;
      try {
        currentPolicy = await authService.getPolicyStatus({ signal: transition.signal });
      } catch {
        currentPolicy = null;
      }
      if (
        transition.signal.aborted ||
        authGenerationRef.current !== transition.generation
      ) {
        return { error: new Error('Sign-in was cancelled.') };
      }
      const session = beginAuthSession(user.id);
      setAuthUser(user);
      setPolicyStatus(currentPolicy);
      if (currentPolicy?.accepted) {
        void loadNotesFromAPI(session);
        void refreshMembership(session);
      }
      setIsAuthLoading(false);
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    } finally {
      if (authGenerationRef.current === transition.generation) {
        setIsAuthLoading(false);
      }
    }
  }, [beginAuthSession, beginAuthTransition, refreshMembership]);

  const signUp = useCallback(async (
    email: string,
    password: string,
    name: string,
    confirmations: PolicyConfirmationsValue,
  ) => {
    const transition = beginAuthTransition();
    try {
      await authService.register(
        email,
        password,
        name,
        confirmations,
        { signal: transition.signal },
      );
      if (
        transition.signal.aborted ||
        authGenerationRef.current !== transition.generation
      ) {
        return { error: new Error('Sign-up was cancelled.') };
      }
      const user = await authService.getCurrentUser({ signal: transition.signal });
      if (
        !user ||
        transition.signal.aborted ||
        authGenerationRef.current !== transition.generation
      ) {
        return { error: new Error('Could not load the new account.') };
      }
      let currentPolicy: PolicyStatus | null = null;
      try {
        currentPolicy = await authService.getPolicyStatus({ signal: transition.signal });
      } catch {
        currentPolicy = null;
      }
      if (
        transition.signal.aborted ||
        authGenerationRef.current !== transition.generation
      ) {
        return { error: new Error('Sign-up was cancelled.') };
      }
      const session = beginAuthSession(user.id);
      setAuthUser(user);
      setPolicyStatus(currentPolicy);
      if (currentPolicy?.accepted) {
        void refreshMembership(session);
      }
      setIsAuthLoading(false);
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    } finally {
      if (authGenerationRef.current === transition.generation) {
        setIsAuthLoading(false);
      }
    }
  }, [beginAuthSession, beginAuthTransition, refreshMembership]);

  const signInWithGoogle = useCallback(async () => {
    return { error: new Error('Google sign-in is not supported in this version') };
  }, []);

  const signInWithApple = useCallback(async () => {
    return { error: new Error('Apple sign-in is not supported in this version') };
  }, []);

  const signOut = useCallback(async () => {
    invalidateAuthSession();
    await authService.logout();
    setIsAuthLoading(false);
    setAuthUser(null);
    setPolicyStatus(null);
    setCurrentEventId(null);
    setNotes([]);
    setSavedChats([]);
    setAgentActions([]);
    setMemberEventIds(new Set());
    tempToRealId.current.clear();
  }, [invalidateAuthSession]);

  const deleteAccount = useCallback(async (
    confirmEmail: string,
    owner: AuthSessionSnapshot,
  ) => {
    if (!isAuthSessionCurrent(owner)) {
      throw new Error('Your account session changed.');
    }
    await authService.deleteAccount(
      confirmEmail,
      { signal: owner.signal },
    );
    if (!isAuthSessionCurrent(owner)) {
      throw new Error('Your account session changed.');
    }

    invalidateAuthSession();
    setIsAuthLoading(false);
    tempToRealId.current.clear();
    setAuthUser(null);
    setPolicyStatus(null);
    setCurrentEventId(null);
    setNotes([]);
    setSavedChats([]);
    setAiPreferences(DEFAULT_AI_PREFS);
    setAgentActions([]);
    setMemberEventIds(new Set());

    try {
      localStorage.removeItem('current-event-id');
      localStorage.removeItem('app-notes');
      localStorage.removeItem('app-ai-chats');
      localStorage.removeItem('app-ai-prefs');
    } catch { /* local state is already cleared in memory */ }
  }, [invalidateAuthSession, isAuthSessionCurrent]);

  const updateProfile = useCallback(async (
    updates: Partial<UserProfile>,
    session: AuthSessionSnapshot,
  ) => {
    if (!isAuthSessionCurrent(session)) {
      return { error: new Error('Not authenticated.') };
    }
    try {
      const updated = await authService.updateProfile(
        updates,
        { signal: session.signal },
      );
      if (isAuthSessionCurrent(session)) {
        setAuthUser(updated);
        return { error: null };
      }
      return { error: new Error('Your account session changed.') };
    } catch (err) {
      return { error: err as Error };
    }
  }, [isAuthSessionCurrent]);

  const refreshUser = useCallback(async (session: AuthSessionSnapshot) => {
    if (!isAuthSessionCurrent(session)) return;
    const user = await authService.getCurrentUser({ signal: session.signal });
    if (user && isAuthSessionCurrent(session)) {
      setAuthUser(user);
    }
  }, [isAuthSessionCurrent]);

  // The upload already committed this URL. Do not depend on a second network
  // read (or issue another PATCH) to stop rendering the deleted previous photo.
  const applyUploadedAvatar = useCallback((url: string, session: AuthSessionSnapshot) => {
    if (!isAuthSessionCurrent(session)) return;
    setAuthUser(current => current?.id === session.userId && isAuthSessionCurrent(session)
      ? { ...current, avatar_url: url }
      : current);
  }, [isAuthSessionCurrent]);

  const refreshPolicyStatus = useCallback(async (session: AuthSessionSnapshot) => {
    if (!isAuthSessionCurrent(session)) return;
    try {
      const status = await authService.getPolicyStatus({ signal: session.signal });
      if (isAuthSessionCurrent(session)) {
        setPolicyStatus(status);
      }
    } catch (error) {
      if (isAuthSessionCurrent(session)) {
        setPolicyStatus(null);
      }
      throw error;
    }
  }, [isAuthSessionCurrent]);

  const acceptCurrentPolicy = useCallback(async (session: AuthSessionSnapshot) => {
    if (!isAuthSessionCurrent(session)) {
      throw new Error('Your account session changed.');
    }
    await authService.acceptCurrentPolicy({ signal: session.signal });
    if (!isAuthSessionCurrent(session)) {
      throw new Error('Your account session changed.');
    }
    await refreshPolicyStatus(session);
    if (!isAuthSessionCurrent(session)) {
      throw new Error('Your account session changed.');
    }
    void loadNotesFromAPI(session);
    void refreshMembership(session);
  }, [isAuthSessionCurrent, refreshMembership, refreshPolicyStatus]);

  // ── Theme ──
  const setTheme = (t: ThemeMode) => setThemeState(t);
  const toggleTheme = () => setThemeState((t) => (t === 'dark' ? 'light' : 'dark'));

  // ── Notes (API-backed with optimistic updates) ──
  const addNote = useCallback((
    note: Omit<SavedNote, 'id' | 'createdAt' | 'updatedAt'>,
    owner: AuthSessionSnapshot,
  ): SavedNote | null => {
    if (!isAuthSessionCurrent(owner)) return null;
    const now = new Date().toISOString();
    const tempId = `temp-${Date.now()}`;
    const newNote: SavedNote = { ...note, id: tempId, createdAt: now, updatedAt: now };
    setNotes((prev) => [newNote, ...prev]);

    if (isAuthSessionCurrent(owner)) {
      noteService.create(
        {
          user_id: owner.userId,
          event_id: currentEventId ?? undefined,
          title: note.title,
          content: note.content,
          source: note.source,
          source_label: note.sourceLabel,
          session_id: note.sessionId,
          paper_id: note.paperId,
        },
        { signal: owner.signal },
      ).then((saved) => {
        if (!isAuthSessionCurrent(owner)) return;
        // Record the mapping so updateNote/deleteNote can resolve the stale temp ID
        tempToRealId.current.set(tempId, saved.id);
        setNotes((prev) =>
          prev.map((n) =>
            n.id === tempId
              ? { ...n, id: saved.id, createdAt: saved.created_at, updatedAt: saved.updated_at }
              : n
          )
        );
      }).catch((err) => {
        console.error('Failed to save note', err);
        if (isAuthSessionCurrent(owner)) {
          setNotes((prev) => prev.filter((n) => n.id !== tempId));
        }
      });
    }

    return newNote;
  }, [currentEventId, isAuthSessionCurrent]);

  const updateNote = useCallback((
    id: string,
    updates: Partial<SavedNote>,
    owner: AuthSessionSnapshot,
  ) => {
    if (!isAuthSessionCurrent(owner)) return;
    const now = new Date().toISOString();
    // Resolve temp ID to real server ID if the API has already responded
    const realId = tempToRealId.current.get(id) ?? id;
    setNotes((prev) =>
      prev.map((n) => (n.id === realId ? { ...n, ...updates, updatedAt: now } : n))
    );
    if (isAuthSessionCurrent(owner)) {
      noteService.update(
        realId,
        { title: updates.title, content: updates.content },
        { signal: owner.signal },
      ).catch((err) => {
        console.error('Failed to update note', err);
      });
    }
  }, [isAuthSessionCurrent]);

  const deleteNote = useCallback((id: string, owner: AuthSessionSnapshot) => {
    if (!isAuthSessionCurrent(owner)) return;
    const realId = tempToRealId.current.get(id) ?? id;
    setNotes((prev) => prev.filter((n) => n.id !== realId));
    if (isAuthSessionCurrent(owner)) {
      noteService.delete(realId, { signal: owner.signal }).catch((err) => {
        console.error('Failed to delete note', err);
      });
    }
  }, [isAuthSessionCurrent]);

  const getNoteById = useCallback((id: string) => notes.find((n) => n.id === id), [notes]);

  // ── AI Chats ──
  const saveChat = useCallback((
    chat: Omit<SavedAIChat, 'id' | 'createdAt'>,
    owner: AuthSessionSnapshot,
  ) => {
    if (!isAuthSessionCurrent(owner)) return;
    const newChat: SavedAIChat = { ...chat, id: `chat-${Date.now()}`, createdAt: new Date().toISOString() };
    setSavedChats((prev) => [newChat, ...prev]);
  }, [isAuthSessionCurrent]);

  const setAiPreference = useCallback(<K extends keyof AIPreferences>(key: K, value: AIPreferences[K]) => {
    setAiPreferences((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Agent Actions ──
  const executeAgentAction = useCallback(
    async (
      action: Omit<AgentAction, 'id' | 'status' | 'createdAt' | 'completedAt'>,
      owner: AuthSessionSnapshot,
    ): Promise<AgentAction> => {
      if (!isAuthSessionCurrent(owner)) {
        throw new Error('Sign in to use agent actions.');
      }
      const newAction: AgentAction = {
        ...action,
        id: `action-${Date.now()}`,
        status: 'running',
        createdAt: new Date().toISOString(),
      };
      setAgentActions((prev) => [newAction, ...prev]);
      try {
        await performAgentAction(action, {
          addNote,
          eventId: currentEventId,
          session: owner,
          isSessionCurrent: isAuthSessionCurrent,
        });
        const completed: AgentAction = { ...newAction, status: 'completed', completedAt: new Date().toISOString() };
        if (isAuthSessionCurrent(owner)) {
          setAgentActions((prev) => prev.map((a) => (a.id === newAction.id ? completed : a)));
        }
        return completed;
      } catch (err) {
        const failed: AgentAction = { ...newAction, status: 'failed', completedAt: new Date().toISOString() };
        if (isAuthSessionCurrent(owner)) {
          setAgentActions((prev) => prev.map((a) => (a.id === newAction.id ? failed : a)));
        }
        throw err instanceof Error ? err : new Error(String(err));
      }
    },
    [addNote, currentEventId, isAuthSessionCurrent]
  );

  const dismissAgentAction = useCallback((id: string) => {
    setAgentActions((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const clearCompletedActions = useCallback(() => {
    setAgentActions((prev) => prev.filter((a) => a.status !== 'completed'));
  }, []);

  // Build legacy user object from profile for existing screens
  const legacyUser: LegacyUser | null = authUser
    ? {
        id: authUser.id,
        name: authUser.name,
        email: authUser.email,
        title: authUser.title,
        company: authUser.company,
        bio: authUser.bio,
        linkedin: authUser.linkedin,
        avatar: authUser.avatar_url,
      }
    : null;

  const value: AppContextType = {
    authUser,
    profile: authUser,
    isAuthenticated: !!authUser,
    isAuthLoading,
    policyStatus,
    authSession,
    isAuthSessionCurrent,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithApple,
    signOut,
    deleteAccount,
    updateProfile,
    refreshUser,
    applyUploadedAvatar,
    acceptCurrentPolicy,
    refreshPolicyStatus,
    currentEventId,
    setCurrentEventId: selectCurrentEvent,
    selectedEvent,
    isSelectedEventLoading,
    memberEventIds,
    isCurrentEventMember: currentEventId ? memberEventIds.has(currentEventId) : false,
    refreshMembership,
    // Backwards-compatible aliases for existing screens
    user: legacyUser,
    setUser: () => { /* managed by JWT auth */ },
    login: (_u: LegacyUser) => { /* screens should use signIn() */ },
    logout: signOut,
    currentEvent: currentEventId,
    setCurrentEvent: selectCurrentEvent,
    theme,
    toggleTheme,
    setTheme,
    notes,
    addNote,
    updateNote,
    deleteNote,
    getNoteById,
    savedChats,
    saveChat,
    aiPreferences,
    setAiPreference,
    agentActions,
    executeAgentAction,
    dismissAgentAction,
    clearCompletedActions,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
